import { useAuth } from '@/contexts/useAuth';
import { moduleRegistry } from '@/config/module-registry.config';
import { quickLinks } from '@/config/quick-links.config';
import { filterModulesByRole } from '@/lib/rbac';
import { groupByCategory } from '@/lib/quick-links';
import type { ModuleEntry } from '@/lib/module-registry';
import ModuleTile from '@/components/ModuleTile';
import LucideIcon from '@/components/LucideIcon';
import { Info, AlertTriangle, ShieldCheck, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Dashboard home page — renders a responsive grid of ModuleTile cards
 * filtered by the authenticated user's role.
 *
 * If the module registry failed to load or parse (empty array when entries
 * were expected), a degraded-state message is shown instead.
 */
export default function DashboardPage() {
  const { role } = useAuth();

  let modules: ModuleEntry[];
  let registryError = false;

  try {
    modules = filterModulesByRole(moduleRegistry, role);
  } catch (err) {
    console.error('[DashboardPage] Failed to filter module registry:', err);
    modules = [];
    registryError = true;
  }

  return (
    <section aria-label="Dashboard">
      <h1 className="mb-6 text-2xl font-semibold">Dashboard</h1>

      {registryError ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-destructive"
          role="alert"
        >
          <AlertTriangle className="size-5 shrink-0" aria-hidden="true" />
          <p>Module information is currently unavailable. Please try again later.</p>
        </div>
      ) : modules.length === 0 ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-dashed p-6 text-muted-foreground"
          role="status"
        >
          <Info className="size-5 shrink-0" aria-hidden="true" />
          <p>No modules are available for your role.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {modules.map((mod) => (
            <ModuleTile key={mod.id} module={mod} />
          ))}
          {role === 'admin' && (
            <Link
              to="/admin"
              className="flex flex-col gap-2 rounded-xl border bg-card p-4 text-card-foreground ring-1 ring-foreground/10 transition-colors hover:bg-muted/50"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
                <span className="font-medium">Admin Console</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Manage users, roles, and system-wide configuration.
              </p>
            </Link>
          )}
        </div>
      )}

      {quickLinks.length > 0 && (
        <div className="mt-8" data-testid="quick-links-section">
          <h2 className="mb-4 text-lg font-semibold">Quick Links</h2>
          {(() => {
            const groups = groupByCategory(quickLinks);
            const hasCategories = Array.from(groups.keys()).some((k) => k !== undefined);

            return Array.from(groups.entries()).map(([category, links]) => (
              <div key={category ?? '__ungrouped'} className="mb-4">
                {category ? (
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">{category}</h3>
                ) : hasCategories ? (
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">Other</h3>
                ) : null}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {links.map((link) => (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 rounded-xl border bg-card p-4 text-card-foreground ring-1 ring-foreground/10 transition-colors hover:bg-muted/50"
                    >
                      <LucideIcon name={link.icon} className="mt-0.5 size-5 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{link.name}</span>
                          <ExternalLink className="size-3 text-muted-foreground" aria-hidden="true" />
                        </div>
                        {link.description && (
                          <p className="mt-0.5 text-sm text-muted-foreground">{link.description}</p>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}
    </section>
  );
}
